import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import type { ReactNode } from 'react';

/**
 * A one-line "done" that goes away on its own.
 *
 * **For success only, and never for a refusal.** `useAction`'s rule stands
 * (`context.tsx`): a refusal is shown where the button is and stays until the
 * next attempt, because the refusals on this surface carry the instruction for
 * what to do instead and a toast is a refusal an operator can miss. What a
 * toast is for is the confirmation an operator does not need to keep —
 * "Love Spa is now live" — where the lasting record is the page itself, which
 * has already re-read from the server.
 *
 * One at a time; a second replaces the first. `role="status"` so a screen
 * reader says it without taking focus.
 *
 * Outside a provider `useToast` returns a no-op rather than throwing: a card
 * rendered on its own (every test in this package renders them that way) has
 * nothing to announce to and should not have to care.
 */
type Show = (message: string) => void;

const ToastContext = createContext<Show>(() => undefined);

export const TOAST_MS = 4000;

export function ToastProvider({ children }: { children: ReactNode }): ReactNode {
  const [message, setMessage] = useState<string | null>(null);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const show = useCallback<Show>((next) => {
    if (timer.current !== null) clearTimeout(timer.current);
    setMessage(next);
    timer.current = setTimeout(() => setMessage(null), TOAST_MS);
  }, []);

  useEffect(
    () => () => {
      if (timer.current !== null) clearTimeout(timer.current);
    },
    [],
  );

  return (
    <ToastContext.Provider value={show}>
      {children}
      <div className="toast-region" role="status" aria-live="polite">
        {message === null ? null : <div className="toast">{message}</div>}
      </div>
    </ToastContext.Provider>
  );
}

export function useToast(): Show {
  return useContext(ToastContext);
}
